import { Check, ShoppingCart } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useState } from "react";
import toast from "react-hot-toast";
import { useCart } from "../../context/CartContext";
import type { Customization, Extra, Product, ProductOptions } from "../../types";
import { cn, formatPrice } from "../../utils/format";
import { Badge } from "../ui/Badge";
import { Button } from "../ui/Button";
import { Modal, ModalCloseButton } from "../ui/Modal";
import { QuantitySelector } from "../ui/QuantitySelector";

const MAX_EXTRA = 10;
const MAX_ITEMS = 20;
const MAX_NOTES = 200;

const round2 = (value: number) => Math.round(value * 100) / 100;

const SectionTitle = ({ title, hint }: { title: string; hint: string }) => (
  <span className="flex w-full items-baseline justify-between gap-3">
    <span className="font-display text-2xl tracking-wide">{title}</span>
    <span className="text-xs text-muted">{hint}</span>
  </span>
);

interface CustomizeFormProps {
  product: Product;
  options: ProductOptions;
  onDone: () => void;
}

const CustomizeForm = ({ product, options, onDone }: CustomizeFormProps) => {
  const { addItem } = useCart();
  const [removed, setRemoved] = useState<Set<number>>(() => new Set());
  const [extraQty, setExtraQty] = useState<Record<number, number>>({});
  const [notes, setNotes] = useState("");
  const [quantity, setQuantity] = useState(1);

  // Tudo que pode ser pedido a mais: ingredientes do próprio lanche + outros adicionais.
  const purchasable: Extra[] = [
    ...options.ingredients.flatMap((i) => (i.extra ? [i.extra] : [])),
    ...options.extras,
  ];
  const extrasTotal = round2(purchasable.reduce((acc, e) => acc + e.price * (extraQty[e.id] ?? 0), 0));
  const unitPrice = round2(product.price + extrasTotal);
  const total = round2(unitPrice * quantity);

  const toggleIngredient = (id: number) => {
    setRemoved((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    // Tirou o ingrediente: zera o "a mais" dele.
    setExtraQty((prev) => ({ ...prev, [id]: 0 }));
  };

  const changeExtra = (id: number, delta: number) =>
    setExtraQty((prev) => ({ ...prev, [id]: Math.min(MAX_EXTRA, Math.max(0, (prev[id] ?? 0) + delta)) }));

  const handleAdd = () => {
    const customization: Customization = {
      removed: options.ingredients.filter((i) => removed.has(i.id)),
      extras: purchasable
        .filter((e) => (extraQty[e.id] ?? 0) > 0)
        .map((e) => ({ ...e, quantity: extraQty[e.id] ?? 0 })),
      notes: notes.trim(),
    };
    addItem(product, customization, quantity);
    toast.success(`${quantity}x ${product.name} adicionado ao carrinho`);
    onDone();
  };

  return (
    <>
      <div className="relative h-44 overflow-hidden bg-night sm:h-52">
        <img src={product.image_url || "/favicon.png"} alt={product.name} className="size-full object-cover" />
        <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-t from-coal via-coal/20 to-transparent" />
        <ModalCloseButton onClick={onDone} className="absolute right-4 top-4" />
      </div>

      <div className="relative -mt-8 px-5 sm:px-7">
        <Badge>{product.category}</Badge>
        <h2 id="customize-title" className="mt-3 font-display text-4xl leading-none tracking-wide sm:text-5xl">
          {product.name}
        </h2>
        {product.description && <p className="mt-2 text-sm leading-relaxed text-muted">{product.description}</p>}
        <p className="mt-3 text-lg font-bold text-primary">{formatPrice(product.price)}</p>
      </div>

      <div className="space-y-8 px-5 py-6 sm:px-7">
        {options.ingredients.length > 0 && (
          <fieldset>
            <legend className="w-full">
              <SectionTitle title="Ingredientes" hint="Desmarque para tirar · + para pedir a mais" />
            </legend>
            <ul className="mt-3 space-y-2">
              {options.ingredients.map((ingredient) => {
                const included = !removed.has(ingredient.id);
                const more = extraQty[ingredient.id] ?? 0;
                return (
                  <li
                    key={ingredient.id}
                    className={cn(
                      "flex items-center gap-3 rounded-xl border px-4 py-2 transition-colors",
                      !included ? "border-danger/30 bg-danger/5" : more > 0 ? "border-primary/40 bg-primary-soft" : "border-line bg-card",
                    )}
                  >
                    <label className="relative flex min-h-11 min-w-0 flex-1 cursor-pointer items-center gap-3 text-sm">
                      <input
                        type="checkbox"
                        className="peer sr-only"
                        checked={included}
                        onChange={() => toggleIngredient(ingredient.id)}
                      />
                      <span
                        aria-hidden="true"
                        className={cn(
                          "grid size-5 shrink-0 place-items-center rounded-md border transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-primary peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-coal",
                          included ? "border-primary bg-primary text-night" : "border-muted/50",
                        )}
                      >
                        {included && <Check className="size-3.5" strokeWidth={3} />}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className={cn("block", !included && "text-muted line-through")}>
                          {ingredient.quantity > 1 && `${ingredient.quantity}x `}
                          {ingredient.name}
                          {more > 0 && <span className="ml-1.5 font-semibold text-primary">+{more}</span>}
                        </span>
                        {!included ? (
                          <span className="text-[11px] font-semibold uppercase tracking-wider text-danger">Sem</span>
                        ) : (
                          ingredient.extra && (
                            <span className="block text-xs text-muted">+ {formatPrice(ingredient.extra.price)} cada a mais</span>
                          )
                        )}
                      </span>
                    </label>
                    {ingredient.extra && included && (
                      <QuantitySelector
                        size="sm"
                        min={0}
                        max={MAX_EXTRA}
                        quantity={more}
                        label={`${ingredient.name} extra`}
                        onIncrement={() => changeExtra(ingredient.id, 1)}
                        onDecrement={() => changeExtra(ingredient.id, -1)}
                      />
                    )}
                  </li>
                );
              })}
            </ul>
          </fieldset>
        )}

        {options.extras.length > 0 && (
          <section aria-labelledby="extras-title">
            <h3 id="extras-title">
              <SectionTitle title="Outros adicionais" hint={`Até ${MAX_EXTRA} de cada`} />
            </h3>
            <ul className="mt-3 divide-y divide-line overflow-hidden rounded-2xl border border-line bg-card">
              {options.extras.map((extra) => {
                const qty = extraQty[extra.id] ?? 0;
                return (
                  <li key={extra.id} className={cn("flex items-center justify-between gap-3 px-4 py-3", qty > 0 && "bg-primary-soft")}>
                    <div className="min-w-0">
                      <p className="text-sm font-medium">{extra.name}</p>
                      <p className="text-xs text-primary">+ {formatPrice(extra.price)}</p>
                    </div>
                    <QuantitySelector
                      size="sm"
                      min={0}
                      max={MAX_EXTRA}
                      quantity={qty}
                      label={extra.name}
                      onIncrement={() => changeExtra(extra.id, 1)}
                      onDecrement={() => changeExtra(extra.id, -1)}
                    />
                  </li>
                );
              })}
            </ul>
          </section>
        )}

        <div>
          <label htmlFor="item-notes" className="block">
            <SectionTitle title="Observação" hint={`${notes.length}/${MAX_NOTES}`} />
          </label>
          <textarea
            id="item-notes"
            maxLength={MAX_NOTES}
            className="field mt-3 h-20 resize-none py-3"
            placeholder="Ex.: carne bem passada"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>
      </div>

      <div className="sticky bottom-0 z-10 border-t border-line bg-coal/95 px-5 pb-[max(1rem,env(safe-area-inset-bottom))] pt-4 backdrop-blur sm:px-7">
        <p className="mb-3 text-xs text-muted" aria-live="polite">
          {formatPrice(product.price)}
          {extrasTotal > 0 && <> + {formatPrice(extrasTotal)} em adicionais</>}
          {quantity > 1 && <> × {quantity}</>} = <strong className="text-white">{formatPrice(total)}</strong>
        </p>
        <div className="flex items-center gap-3">
          <QuantitySelector
            min={1}
            max={MAX_ITEMS}
            quantity={quantity}
            label={product.name}
            onIncrement={() => setQuantity((q) => Math.min(MAX_ITEMS, q + 1))}
            onDecrement={() => setQuantity((q) => Math.max(1, q - 1))}
          />
          <Button
            size="lg"
            onClick={handleAdd}
            icon={<ShoppingCart className="size-5" aria-hidden="true" />}
            iconPosition="left"
            className="min-w-0 flex-1 px-4"
          >
            <span className="hidden sm:inline">Adicionar ao carrinho</span>
            <span className="sm:hidden">Adicionar</span>
            <span aria-hidden="true">·</span>
            <AnimatePresence mode="popLayout" initial={false}>
              <motion.span
                key={total}
                initial={{ y: -8, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: 8, opacity: 0 }}
                transition={{ duration: 0.15 }}
                className="tabular-nums"
              >
                {formatPrice(total)}
              </motion.span>
            </AnimatePresence>
          </Button>
        </div>
      </div>
    </>
  );
};

interface CustomizeModalProps {
  open: boolean;
  /** Muda a cada abertura para começar a personalização do zero. */
  sessionKey: number;
  product: Product | null;
  options: ProductOptions | null;
  onClose: () => void;
}

export const CustomizeModal = ({ open, sessionKey, product, options, onClose }: CustomizeModalProps) => (
  <Modal open={open} onClose={onClose} labelledBy="customize-title" className="sm:max-w-xl">
    {product && options && <CustomizeForm key={sessionKey} product={product} options={options} onDone={onClose} />}
  </Modal>
);
